import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ragApi, RagApiError } from '../services/ApiService'
import type {
  Category,
  ApiCategory,
  ApiSubcategory,
  CategoryCreateData,
  SubcategoryCreateData,
  SubcategoryUpdateData,
  ApiDocument,
} from '../lib/types'
import { queryKeys } from '@/queries/keys'

interface UseCategoriesResult {
  categories: Category[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  createCategory: (data: CategoryCreateData) => Promise<void>
  updateCategory: (id: string, data: Partial<CategoryCreateData>) => Promise<void>
  deleteCategory: (id: string) => Promise<void>
  createSubcategory: (data: SubcategoryCreateData) => Promise<void>
  updateSubcategory: (id: string, data: Partial<SubcategoryUpdateData>) => Promise<void>
  deleteSubcategory: (id: string) => Promise<void>
}

type CategoriesBundle = {
  categories: ApiCategory[]
  subcategories: ApiSubcategory[]
  documents: ApiDocument[]
}

function mapBundleToCategories(bundle: CategoriesBundle): Category[] {
  const subcategoriesByCategory: Record<number, ApiSubcategory[]> = {}
  bundle.subcategories.forEach((sub) => {
    if (!subcategoriesByCategory[sub.category]) subcategoriesByCategory[sub.category] = []
    subcategoriesByCategory[sub.category].push(sub)
  })

  const itemCounts: Record<number, number> = {}
  bundle.documents.forEach((doc) => {
    if (doc.category) itemCounts[doc.category] = (itemCounts[doc.category] || 0) + 1
    if (doc.subcategory) itemCounts[doc.subcategory] = (itemCounts[doc.subcategory] || 0) + 1
  })

  return bundle.categories.map((apiCategory) => ({
    id: apiCategory.id.toString(),
    name: apiCategory.name,
    subcategories: (subcategoriesByCategory[apiCategory.id] || []).map((sub) => ({
      id: sub.id.toString(),
      name: sub.name,
      subcategories: [],
      itemCount: itemCounts[sub.id] || 0,
    })),
    itemCount: itemCounts[apiCategory.id] || 0,
  }))
}

function useCategoriesBundleQuery() {
  return useQuery({
    queryKey: queryKeys.knowledge.categoriesBundle(),
    queryFn: async (): Promise<CategoriesBundle> => {
      const [categoriesResp, subcategoriesResp, documentsResp] = await Promise.all([
        ragApi.getCategories() as Promise<ApiCategory[]>,
        ragApi.getSubcategories().catch(() => []) as Promise<ApiSubcategory[]>,
        ragApi.getDocuments().catch(() => []) as Promise<ApiDocument[]>,
      ])

      return {
        categories: Array.isArray(categoriesResp) ? categoriesResp : [],
        subcategories: Array.isArray(subcategoriesResp) ? subcategoriesResp : [],
        documents: Array.isArray(documentsResp) ? documentsResp : [],
      }
    },
    staleTime: 120_000,
    refetchOnMount: false,
    retry: 0,
  })
}

/**
 * Hook for managing categories and subcategories
 */
export function useCategories(): UseCategoriesResult {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [mutationError, setMutationError] = useState<string | null>(null)

  const bundleQuery = useCategoriesBundleQuery()

  const categories = useMemo(
    () => mapBundleToCategories(bundleQuery.data || { categories: [], subcategories: [], documents: [] }),
    [bundleQuery.data]
  )

  const createCategoryMutation = useMutation({
    mutationFn: async (data: CategoryCreateData) => {
      await ragApi.createCategory(data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.categoriesBundle() })
    },
  })

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CategoryCreateData> }) => {
      await ragApi.updateCategory(id, data)
      return { id, data }
    },
    onSuccess: ({ id, data }) => {
      queryClient.setQueryData<CategoriesBundle>(queryKeys.knowledge.categoriesBundle(), (old) => {
        if (!old) return old
        return {
          ...old,
          categories: old.categories.map((category) =>
            String(category.id) === id ? { ...category, ...data } : category
          ),
        }
      })
    },
  })

  const createSubcategoryMutation = useMutation({
    mutationFn: async (data: SubcategoryCreateData) => {
      await ragApi.createSubcategory(data)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.categoriesBundle() })
    },
  })

  const updateSubcategoryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<SubcategoryUpdateData> }) => {
      await ragApi.updateSubcategory(id, data)
      return { id, data }
    },
    onSuccess: ({ id, data }) => {
      queryClient.setQueryData<CategoriesBundle>(queryKeys.knowledge.categoriesBundle(), (old) => {
        if (!old) return old
        return {
          ...old,
          subcategories: old.subcategories.map((sub) =>
            String(sub.id) === id ? { ...sub, ...data } : sub
          ),
        }
      })
    },
  })

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await ragApi.deleteCategory(id)
      return id
    },
    onSuccess: async (id) => {
      queryClient.setQueryData<CategoriesBundle>(queryKeys.knowledge.categoriesBundle(), (old) => {
        if (!old) return old
        const categoryId = Number(id)
        return {
          ...old,
          categories: old.categories.filter((category) => category.id !== categoryId),
          subcategories: old.subcategories.filter((sub) => sub.category !== categoryId),
        }
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.categoriesBundle() })
    },
  })

  const deleteSubcategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await ragApi.deleteSubcategory(id)
      return id
    },
    onSuccess: async (id) => {
      queryClient.setQueryData<CategoriesBundle>(queryKeys.knowledge.categoriesBundle(), (old) => {
        if (!old) return old
        return {
          ...old,
          subcategories: old.subcategories.filter((sub) => String(sub.id) !== id),
        }
      })
      await queryClient.invalidateQueries({ queryKey: queryKeys.knowledge.categoriesBundle() })
    },
  })

  const createCategory = useCallback(async (data: CategoryCreateData) => {
    try {
      setMutationError(null)
      await createCategoryMutation.mutateAsync(data)
    } catch (err) {
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to create category'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [createCategoryMutation])

  const updateCategory = useCallback(async (id: string, data: Partial<CategoryCreateData>) => {
    try {
      setMutationError(null)
      await updateCategoryMutation.mutateAsync({ id, data })
    } catch (err) {
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to update category'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [updateCategoryMutation])

  const createSubcategory = useCallback(async (data: SubcategoryCreateData) => {
    try {
      setMutationError(null)
      await createSubcategoryMutation.mutateAsync(data)
    } catch (err) {
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to create subcategory'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [createSubcategoryMutation])

  const updateSubcategory = useCallback(async (id: string, data: Partial<SubcategoryUpdateData>) => {
    try {
      setMutationError(null)
      await updateSubcategoryMutation.mutateAsync({ id, data })
    } catch (err) {
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to update subcategory'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [updateSubcategoryMutation])

  const deleteCategory = useCallback(async (id: string) => {
    try {
      setMutationError(null)
      const category = categories.find((cat) => cat.id === id)
      if (category && category.itemCount > 0) return
      await deleteCategoryMutation.mutateAsync(id)
    } catch (err) {
      if (err instanceof RagApiError && err.status === 500) {
        const errorMessage = t('knowledge.categories.error_cannot_delete_with_documents', {
          name: categories.find((category) => category.id === id)?.name || 'categoría',
        })
        setMutationError(errorMessage)
        throw new Error(errorMessage)
      }
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to delete category'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [categories, deleteCategoryMutation, t])

  const deleteSubcategory = useCallback(async (id: string) => {
    try {
      setMutationError(null)
      const subcategory = categories
        .flatMap((category) => category.subcategories)
        .find((sub) => sub.id === id)
      if (subcategory && subcategory.itemCount > 0) return
      await deleteSubcategoryMutation.mutateAsync(id)
    } catch (err) {
      const errorMessage = err instanceof RagApiError ? err.message : 'Failed to delete subcategory'
      setMutationError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [categories, deleteSubcategoryMutation])

  return {
    categories,
    loading: bundleQuery.isLoading,
    error: mutationError || (bundleQuery.error instanceof Error ? bundleQuery.error.message : null),
    refetch: async () => {
      await bundleQuery.refetch()
    },
    createCategory,
    updateCategory,
    deleteCategory,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
  }
}

/**
 * Hook for getting category options for forms
 */
export function useCategoryOptions() {
  const { categories, loading, error } = useCategories()

  const categoryOptions = categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    subcategories: cat.subcategories,
  }))

  const getCategoryById = useCallback(
    (id: string) => {
      return categories.find((cat) => cat.id === id)
    },
    [categories]
  )

  const getSubcategoryById = useCallback(
    (id: string) => {
      for (const category of categories) {
        const subcategory = category.subcategories.find((sub) => sub.id === id)
        if (subcategory) return subcategory
      }
      return null
    },
    [categories]
  )

  return {
    categoryOptions,
    categories,
    loading,
    error,
    getCategoryById,
    getSubcategoryById,
  }
}

/**
 * Hook for category management with simplified form interface (for CategoryManager component)
 */
export function useCategoryManager() {
  const { t } = useTranslation()
  const {
    categories,
    loading,
    error,
    refetch,
    createCategory,
    updateCategory,
    deleteCategory,
    createSubcategory,
    updateSubcategory,
    deleteSubcategory,
  } = useCategories()

  const handleAddCategory = useCallback(
    async (parentId: string | null, name: string) => {
      if (!parentId) {
        await createCategory({ name })
      } else {
        await createSubcategory({ name, category: parseInt(parentId, 10) })
      }
    },
    [createCategory, createSubcategory]
  )

  const handleDeleteSubcategory = useCallback(
    async (id: string) => {
      const subcategory = categories
        .flatMap((category) => category.subcategories)
        .find((sub) => sub.id === id)
      if (subcategory && subcategory.itemCount > 0) return

      try {
        await deleteSubcategory(id)
      } catch (error) {
        if (error instanceof RagApiError && error.status === 500) {
          const errorMessage = t('knowledge.categories.error_cannot_delete_subcategory_with_documents', {
            name: subcategory?.name || 'subcategoría',
          })
          throw new Error(errorMessage)
        }
        throw error
      }
    },
    [categories, deleteSubcategory, t]
  )

  const handleDeleteCategory = useCallback(
    async (id: string) => {
      const category = categories.find((cat) => cat.id === id)
      if (category) {
        if (category.itemCount > 0) return
        for (const subcategory of category.subcategories) {
          if (subcategory.itemCount > 0) return
        }
        for (const subcategory of category.subcategories) {
          await handleDeleteSubcategory(subcategory.id)
        }
      }
      await deleteCategory(id)
    },
    [categories, deleteCategory, handleDeleteSubcategory]
  )

  const handleUpdateCategory = useCallback(
    async (id: string, updatedData: Partial<{ name: string }>) => {
      const isSubcategory = categories.some((category) =>
        category.subcategories.some((subcategory) => subcategory.id === id)
      )
      if (isSubcategory) {
        await updateSubcategory(id, updatedData)
      } else {
        await updateCategory(id, updatedData)
      }
    },
    [categories, updateCategory, updateSubcategory]
  )

  const handleMoveCategory = useCallback(() => {
    // Ordering/hierarchy reordering is not implemented on backend yet.
  }, [])

  const handleSaveCategories = useCallback(() => {
    // Persisted immediately via API mutations.
  }, [])

  const handleCancelCategories = useCallback(() => {
    void refetch()
  }, [refetch])

  return {
    categories,
    loading,
    error,
    onAddCategory: handleAddCategory,
    onDeleteCategory: handleDeleteCategory,
    onDeleteSubcategory: handleDeleteSubcategory,
    onUpdateCategory: handleUpdateCategory,
    onUpdateSubcategory: updateSubcategory,
    onMoveCategory: handleMoveCategory,
    onSave: handleSaveCategories,
    onCancel: handleCancelCategories,
  }
}
